import { useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  RemovePriorYearTaxDocumentMutation,
  RemovePriorYearTaxDocumentMutationVariables,
  REMOVE_PRIOR_YEAR_TAX_DOCUMENT,
  DataSourcesTableItem,
  useCurrentCompanyOrFail,
} from '@neo.tax/shared';

import { GeneralFormErrorStyled, ModalContainer } from './delete-prior-fiscal-year-document.styled';

interface DeletePriorFiscalYearDocumentProps {
  itemToDelete: DataSourcesTableItem;
  close: () => void;
}

export const DeletePriorFiscalYearDocument = ({
  close,
  itemToDelete,
}: DeletePriorFiscalYearDocumentProps): React.ReactElement | null => {
  if (itemToDelete.itemType !== 'tax-document') {
    throw createError('DeletePriorFiscalYearDocumentError', 'This component can only be used to delete tax document.');
  }
  const fileUploadId = itemToDelete.id;
  const company = useCurrentCompanyOrFail();

  const [removePriorYearTaxDocument, { loading }] = useMutation<
    RemovePriorYearTaxDocumentMutation,
    RemovePriorYearTaxDocumentMutationVariables
  >(REMOVE_PRIOR_YEAR_TAX_DOCUMENT);

  const [errorMessage, setErrorMessage] = useState<string>();

  const onSubmit = async (): Promise<void> => {
    try {
      await removePriorYearTaxDocument({
        variables: {
          fileUploadId,
        },
        optimisticResponse: {
          removePriorYearTaxDocument: {
            __typename: 'Company',
            id: company.id,
            bulkPriorYearTaxDocumentUploads: company.bulkPriorYearTaxDocumentUploads.filter(
              (upload) => upload.id !== fileUploadId,
            ),
          },
        },
      });
      close();
    } catch {
      setErrorMessage('Something unexpected prevented the file from being deleted. Please try again.');
    }
  };

  return (
    <ModalContainer>
      <AlertModalLayout
        title={`Delete ${itemToDelete.title}`}
        copy={`Are you sure you want to delete ${itemToDelete.title}?`}
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={close}>
              Cancel
            </Button>
            <Button disabled={loading} rounded="regular" size="base" type="submit" variant="rsbry" onClick={onSubmit}>
              {loading ? 'Deleting...' : 'Confirm'}
            </Button>
          </>
        }
      >
        <GeneralFormErrorStyled centered error={!!errorMessage}>
          {errorMessage}
        </GeneralFormErrorStyled>
      </AlertModalLayout>
    </ModalContainer>
  );
};
